import { useId } from "react";

interface Props {
  /** Section heading ("Who are these gifts from?"). */
  heading?: string;
  firstName: string;
  onFirstName: (v: string) => void;
  lastName: string;
  onLastName: (v: string) => void;
  company: string;
  onCompany: (v: string) => void;
  /** Explanatory line under the fields — the gift page uses these to say who the gift is from. */
  helper: string;
  /**
   * Optional campaign-level signature (Universal Link). When `onSignature` is supplied the field
   * renders; its value is the unboxing page's `@Signature`, prefilled with the
   * `@SenderFirstName at @SenderCompany` tokens (resolved against the fields above at redemption).
   */
  signature?: string;
  onSignature?: (v: string) => void;
  /** Spacing from the element above (caller controls the layout gap). */
  className?: string;
}

/**
 * The campaign-level sender block (first [required] / last [optional] / company [required], plus an
 * optional signature) that feeds the unboxing page's @SenderFirstName / @SenderCompany / @Signature
 * tokens when there are no recipient rows. Shared by Manual Distribution (Path A) and Universal Link.
 * Field ids are generated per instance (`useId`) so two mounted copies — Manual Path A and Universal,
 * both live in the DOM at once inside their collapsed panels — never collide (duplicate ids let a
 * label focus the wrong input and fail an a11y audit).
 */
export default function SenderInfoFields({
  heading = "Who are these gifts from?",
  firstName,
  onFirstName,
  lastName,
  onLastName,
  company,
  onCompany,
  helper,
  signature,
  onSignature,
  className,
}: Props) {
  const uid = useId();
  return (
    <div className={className}>
      <p className="font-lato text-[14px] font-semibold text-rodeo-black mb-2">{heading}</p>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        <SenderField
          id={`${uid}-first`}
          label="Sender first name"
          value={firstName}
          onChange={onFirstName}
        />
        <SenderField
          id={`${uid}-last`}
          label="Sender last name (optional)"
          value={lastName}
          onChange={onLastName}
        />
        <SenderField
          id={`${uid}-company`}
          label="Sender company"
          value={company}
          onChange={onCompany}
        />
      </div>
      <p className="font-lato text-[12px] text-graphite mt-2 leading-relaxed">{helper}</p>

      {onSignature && (
        <div className="mt-4">
          <SenderField
            id={`${uid}-signature`}
            label="Signature (optional)"
            value={signature ?? ""}
            onChange={onSignature}
          />
          <p className="font-lato text-[12px] text-graphite mt-2 leading-relaxed">
            Fill in the sender details above to preview it.
          </p>
        </div>
      )}
    </div>
  );
}

function SenderField({
  id,
  label,
  value,
  onChange,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={id} className="font-lato text-[12px] text-graphite">
        {label}
      </label>
      <input
        id={id}
        type="text"
        autoComplete="off"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="font-lato text-[14px] text-rodeo-black px-3 py-2 rounded-btn outline-none transition-colors bg-white border border-smoke focus:border-dark-dust"
      />
    </div>
  );
}
